import React, { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import LeftSidebar from './LeftSidebar';
import RightSidebar from './RightSidebar';
import MobileBottomNav from './MobileBottomNav';
import SupportTicker from './SupportTicker';
import MobileAdStrip from '../advertising/MobileAdStrip';
import { useAppSelector } from '../../hooks/redux';
import { socialService } from '../../services/socialService';
import { useMediaQuery } from '../../hooks/useResponsive';

interface MainLayoutProps {
  showSidebars?: boolean;
}

const MainLayout: React.FC<MainLayoutProps> = ({ showSidebars = true }) => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const location = useLocation();
  const isSupportTicketRoute = location.pathname.startsWith('/support/tickets');
  const hasDesktopNavigation = useMediaQuery('(min-width: 1024px)');
  const hasDesktopAdSidebar = useMediaQuery('(min-width: 1280px)');
  const showFooter = !isAuthenticated || !showSidebars;

  /* ── Global heartbeat: keep user online status alive ──────── */
  useEffect(() => {
    if (!isAuthenticated) return;
    socialService.sendHeartbeat();
    const interval = setInterval(() => socialService.sendHeartbeat(), 120_000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  return (
    /**
     * Root: viewport-height flex column, no overflow on root so 
     * nothing bleeds past the viewport.
     */
    <div className="flex flex-col bg-white dark:bg-[#0a0e27]" style={{ height: '100dvh', overflow: 'hidden' }}>

      {/* ── Sticky top bars (never scroll) ─────────────────────── */}
      <div className="flex-shrink-0 z-50">
        <SupportTicker />
        <Header />
      </div>

      {/* ── Body row — fills remaining height ──────────────────── */}
      <div className="flex flex-1 min-h-0 w-full max-w-[1800px] mx-auto px-2 sm:px-3 lg:px-5">

        {/* LEFT SIDEBAR — independent scroll, hidden on mobile */}
        {showSidebars && isAuthenticated && hasDesktopNavigation && (
          <div className="hidden lg:flex flex-col w-56 xl:w-64 flex-shrink-0 overflow-y-auto py-3 pr-1 scrollbar-none">
            <LeftSidebar embedded />
          </div>
        )}

        {/* MAIN CONTENT — each page scrolls here independently */}
        <main
          className="flex-1 min-w-0 overflow-y-scroll py-3 pb-24 lg:pb-6 scrollbar-none overscroll-y-contain"
          id="main-scroll-container"
          style={{ WebkitOverflowScrolling: 'touch', overscrollBehaviorY: 'contain' }}
        >
          {/* On mobile no inner padding — outer body row px-2 is enough.
              sm+ gets a small additional inset so cards don't touch sidebar. */}
          <div className="sm:px-2 lg:px-3">
            {/* Visible sponsored strip on mobile/tablet (hidden at xl where RightSidebar shows ads). */}
            {showSidebars && isAuthenticated && !isSupportTicketRoute && !hasDesktopAdSidebar && <MobileAdStrip />}
            <Outlet />
          </div>
          {showFooter && <Footer />}
        </main>


        {/* RIGHT SIDEBAR (ads) — independent scroll, xl+ only */}
        {showSidebars && isAuthenticated && hasDesktopAdSidebar && (
          <div className="hidden xl:flex flex-col w-64 2xl:w-72 flex-shrink-0 overflow-y-auto py-3 pl-1 scrollbar-none">
            <RightSidebar embedded />
          </div>
        )}
      </div>

      {/* ── Mobile bottom nav (fixed, always on top) ───────────── */}
      {showSidebars && !hasDesktopNavigation && <MobileBottomNav />}

    </div>
  );
};

export default MainLayout;
